'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import client from '@/lib/api/client'
import { MainCategory } from '@/lib/categories/categories'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu'
import { useParams, useSearchParams } from 'next/navigation'
import { useRouter } from 'nextjs-toploader/app'
import { useUserContext } from '@/lib/contexts/UserContext'
import { cn } from '@/lib/utils'
import Image from 'next/image'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { SimpleSearchBar } from '@/components/ui/simple-search-bar'
import Link from 'next/link'
import { SearchIcon } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { useRafInterval } from 'ahooks'
import { MIN_HEADER_HEIGHT } from '@/lib/config'

export interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  onResultClick?: (result: any) => void
  slim?: boolean
}

const manifestBaseUrl = process.env.NEXT_PUBLIC_HEADER_BASE_URL || 'https://user.productioncrate.com'
const manifestUrl = manifestBaseUrl + '/asset-manifest.json'

interface Manifest {
  'header.css': string
  'header.js': string
}

const Header: React.FC = () => {
  const [manifest, setManifest] = useState<Manifest | null>(null)
  const [height, setHeight] = useState(0)
  const headerRootRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Fetch the manifest.json file
    const fetchManifest = async () => {
      try {
        const response = await fetch(manifestUrl)
        const data = await response.json()
        // Set the manifest data into the state
        setManifest({
          'header.css': data.files['header.css'],
          'header.js': data.files['header.js'],
        })
      } catch (error) {
        console.error('Error fetching the manifest:', error)
      }
    }

    fetchManifest()
  }, [])

  useEffect(() => {
    // Dynamically load the CSS file
    if (manifest?.['header.css']) {
      const link = document.createElement('link')
      link.href = manifestBaseUrl + manifest['header.css']
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    }

    // Dynamically load the JS file
    if (manifest?.['header.js']) {
      const script = document.createElement('script')
      script.src = manifestBaseUrl + manifest['header.js']
      script.async = true
      script.onload = () => {
        // Optional: Do something when the script has loaded
        console.debug('Header script loaded.')
      }
      document.body.appendChild(script)
    }
  }, [manifest])

  // Keep track of header size every 100ms
  const clearInterval = useRafInterval(() => {
    if (headerRootRef.current) {
      const headerElement = headerRootRef.current.querySelector('.header')
      const headerHeight = headerElement?.getBoundingClientRect().height

      if (headerHeight && headerHeight !== height) {
        setHeight(headerHeight)
      }
    }
  }, 100)

  useEffect(() => {
    // Cleanup the RAF interval
    return () => {
      clearInterval()
    }
  }, [clearInterval])

  const calculatedHeaderHeight = useMemo(() => {
    const calculated = Math.max(MIN_HEADER_HEIGHT, height)
    console.debug('Header height changed:', height, 'Calculated:', calculated)
    return calculated
  }, [height])

  // Sync height
  useEffect(() => {
    const root = document.querySelector(':root') as HTMLElement

    if (root) {
      root.style.setProperty('--header-height', `${calculatedHeaderHeight}px`)
      root.style.setProperty('--header-height', `${calculatedHeaderHeight}px`)
    }
  }, [calculatedHeaderHeight])

  return (
    <div
      id="header-root"
      style={{
        minHeight: calculatedHeaderHeight,
      }}
      ref={headerRootRef}
      data-new-url-scheme="1"
      data-search-callback="pcSearchCallback"
    ></div>
  )
}
Header.displayName = 'Header'

// TODO: Convert Tailwind classes to css modules
const Header2 = React.forwardRef<HTMLDivElement, HeaderProps>(({ className, ...props }, ref) => {
  const router = useRouter()

  const params = useParams<{
    mainCategory?: string
    category?: string
    query?: string
  }>()

  const searchParams = useSearchParams()

  useEffect(() => {}, [])
  const { mainCategory: mainCategorySlug, category: categorySlug, query: encodedQuery } = params

  const query = decodeURIComponent(encodedQuery ?? '')

  const [searchValue, setSearchValue] = useState(query)
  const [showSearchResults, setShowSearchResults] = useState(false)

  const { data: mainCategories, isLoading: isLoadingMainCategories } = useQuery({
    queryKey: ['mainCategories'],
    queryFn: ({ signal }) =>
      client
        .get<MainCategory[]>(`/main-categories`, {
          signal,
        })
        .then(({ data }) => data),
  })

  const handleSearchValueChange = useCallback(
    (value: string) => {
      if (value === searchValue) {
        return
      }

      setSearchValue(value)
      setShowSearchResults(!!value)
    },
    [searchValue]
  )

  const handleSearch = useCallback(
    (value: string) => {
      let href = `/search`

      // If a main category is selected in path or query, add it to the search link
      const resolvedMainCategorySlug = mainCategorySlug ?? searchParams.get('main_category') ?? null

      if (resolvedMainCategorySlug) {
        href += `/${resolvedMainCategorySlug}`
      }

      href += `/${encodeURIComponent(value)}`

      router.push(href)
    },
    [mainCategorySlug, router, searchParams]
  )

  const { user, isAuthenticating } = useUserContext()

  // Debug search value
  useEffect(() => {
    console.debug('Search query:', query)
  }, [query])

  return (
    <div className="tw-flex tw-h-auto tw-w-full tw-flex-col tw-items-center tw-justify-start tw-gap-3 tw-bg-[var(--Primary,#14151C)] tw-p-3">
      <div className="tw-container tw-flex tw-w-full tw-flex-row tw-items-center">
        <div className="tw-flex-[1]">
          <Link href="/" className="tw-flex tw-flex-row tw-items-center tw-space-x-3">
            <Image
              src="/img/isotype.svg"
              width={0}
              height={0}
              style={{
                width: '25px',
                height: '32px',
              }}
              alt="Production Crate logo"
            />
            <div className="tw-color-[var(--Primary-100)] tw-text-2xl">
              Production<span className="tw-font-bold">Crate</span>
            </div>
          </Link>
        </div>
        <div className="tw-flex-[3]">
          {/*<SearchBar*/}
          {/*  placeHolder="Try searching “new” to see recent uploads"*/}
          {/*  className="tw-w-[512px] tw-bg-[#000]"*/}
          {/*  style={{ background: '#000' }}*/}
          {/*/>*/}
          <Popover open={showSearchResults} onOpenChange={(open) => setShowSearchResults(open)}>
            <PopoverTrigger asChild>
              <SimpleSearchBar
                defaultValue={query ?? ''}
                className="tw-mx-auto tw-w-full tw-max-w-[680px]"
                placeholder="Try searching “new” to see recent uploads"
                onValueChange={handleSearchValueChange}
                onSearch={handleSearch}
              />
            </PopoverTrigger>
            <PopoverContent
              className="tw-w-full tw-border-[var(--Primary-400)] !tw-bg-[var(--Primary)] tw-text-[var(--Primary-100)]"
              onOpenAutoFocus={(event) => {
                event.preventDefault()
              }}
            >
              <div className="tw-flex tw-flex-row tw-items-start tw-justify-items-stretch tw-space-x-4">
                {React.Children.toArray(
                  mainCategories?.map((mainCategory, index) => {
                    return (
                      <>
                        <div>
                          <h3 className="tw-opacity-50">{mainCategory.name}</h3>
                          <Link
                            href={`/search/${mainCategory.slug}/${encodeURIComponent(searchValue)}`}
                            className="tw-group tw-flex tw-flex-row tw-items-center tw-space-x-2"
                          >
                            <SearchIcon size={16} />
                            <div className="group-hover:tw-underline">
                              Search {mainCategory.name} for &quot;{searchValue}&quot;
                            </div>
                          </Link>
                        </div>
                        {index !== mainCategories.length - 1 && (
                          <Separator orientation="vertical" className="tw-bg-[var(--Primary-400)]" />
                        )}
                      </>
                    )
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="tw-flex-[1]">
          {!isAuthenticating && !user && (
            <div className="tw-flex tw-flex-row tw-justify-end tw-space-x-2">
              <Link href={`/login`}>
                <Button variant="stroke">LOG IN</Button>
              </Link>
              <Link href={`https://www.productioncrate.com/register/`}>
                <Button>SUBSCRIBE</Button>
              </Link>
            </div>
          )}
          {!isAuthenticating && user && (
            <div className="tw-flex tw-flex-row tw-justify-end tw-space-x-2">
              <Link href={`/member`}>
                <Button variant="stroke">{user.username}</Button>
              </Link>
              <Link href={`/logout`}>
                <Button>LOGOUT</Button>
              </Link>
            </div>
          )}
          {isAuthenticating && (
            <div className="tw-flex tw-flex-row tw-justify-end tw-space-x-2">Getting session...</div>
          )}
        </div>
      </div>

      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
        {isLoadingMainCategories && (
          <div className="tw-flex tw-flex-row tw-items-center tw-space-x-4 tw-py-2">
            <Skeleton className="tw-h-6 tw-w-8" />
            <Skeleton className="tw-h-6 tw-w-8" />
            <Skeleton className="tw-h-6 tw-w-8" />
          </div>
        )}
        {!isLoadingMainCategories && (
          <NavigationMenu>
            <NavigationMenuList>
              {React.Children.toArray(
                mainCategories?.map((mainCategory) => {
                  return (
                    <NavigationMenuItem>
                      <NavigationMenuTrigger
                        className={cn({
                          'tw-text-[var(--Secondary)]': mainCategorySlug === mainCategory.slug,
                        })}
                        onClick={() => {
                          router.push(`/${mainCategory.slug}`)
                        }}
                      >
                        {mainCategory.name}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent className="tw-z-20">
                        <NavigationMenuList className="body_3 tw-min-w-80 tw-flex-col tw-border-0 tw-bg-[var(--Primary)]">
                          {React.Children.toArray(
                            mainCategory.categories?.map((category) => {
                              return (
                                <NavigationMenuItem
                                  className={cn(
                                    'tw-w-full tw-border-b tw-border-[var(--Primary-300)] last:tw-border-0',
                                    {
                                      'tw-text-[var(--Secondary)]':
                                        mainCategorySlug === mainCategory.slug && categorySlug === category.slug,
                                    }
                                  )}
                                >
                                  <NavigationMenuLink asChild>
                                    <Link
                                      href={`/${mainCategory.slug}/${category.slug}`}
                                      className="tw-block tw-w-full tw-p-3 tw-transition-all hover:tw-text-[var(--Secondary)]"
                                    >
                                      {category.name}
                                    </Link>
                                  </NavigationMenuLink>
                                </NavigationMenuItem>
                              )
                            })
                          )}
                        </NavigationMenuList>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  )
                })
              )}
            </NavigationMenuList>
          </NavigationMenu>
        )}
      </div>
    </div>
  )
})
Header2.displayName = 'Header2'

export { Header }
